import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Switch, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, useFocusableGroup } from '@fluentui/react-components';
import { MoreHorizontalFilled } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { Notification, NotificationLocationCode } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import './NotificationsTable.scss';

interface Props {
  title: string;
  notificationLocationCode: NotificationLocationCode;
  onEditNotification(locationCode: NotificationLocationCode, notification: Notification): void;
  onEditNotificationStatus(notificationId: number, newValue: boolean): void;
  onRemoveNotification(notification: Notification): void;
}

const NotificationsTableImpl: React.FC<Props> = props => {
  const {
    title,
    notificationLocationCode,
    onEditNotification,
    onEditNotificationStatus,
    onRemoveNotification
  } = props;

  const {
    notificationPageUiStore
  } = useStores();

  const focusableGroupAttr = useFocusableGroup({
    tabBehavior: 'limited-trap-focus',
  });


  const onEditNotificationStatusClicked = (notification: Notification, newValue: boolean) => {
    if (newValue && !notificationPageUiStore.canActivateMoreNotifications(notificationLocationCode)) {
      notificationPageUiStore.setIsErrorToastOpen(true);
      return;
    }
    onEditNotificationStatus(notification.notificationId, newValue);
  }

  const onEditNotificationClicked = (notification: Notification) => {
    onEditNotification(notificationLocationCode, notification);
  }

  const onDeleteNotificationClicked = (notification: Notification) => {
    onRemoveNotification(notification);
  }

  return (
    <div className='notification-table'>
      <FlexBox
        content='space-between'
      >
        <Text
          size={500}
          weight='semibold'
        >
          {title}
        </Text>
        <Text italic>You may enable up to 2 notifications at a time</Text>
      </FlexBox>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Message</TableHeaderCell>
            <TableHeaderCell>Type of notification</TableHeaderCell>
            <TableHeaderCell className='col-switcher'>Enable / disable</TableHeaderCell>
            <TableHeaderCell className='col-menu' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {
            notificationPageUiStore.notificationsByLocation(notificationLocationCode)?.length ?
              notificationPageUiStore.notificationsByLocation(notificationLocationCode).map((notification, index) => {
                return (
                  <TableRow
                    key={index}
                  >
                    <TableCell>{notification.message}</TableCell>
                    <TableCell>{notification.displayName}</TableCell>
                    <TableCell
                      {...focusableGroupAttr}
                      tabIndex={0}
                      className='col-switcher'
                    >
                      <Switch
                        label={notification.isActive ? 'Yes' : 'No'}
                        checked={notification.isActive}
                        onChange={(_, data) => onEditNotificationStatusClicked(notification, data.checked)}
                      />
                    </TableCell>
                    <TableCell
                      {...focusableGroupAttr}
                      tabIndex={0}
                      className='col-menu'
                    >
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            title='more'
                            appearance='subtle'
                            icon={<MoreHorizontalFilled />}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem onClick={() => onEditNotificationClicked(notification)}>Edit</MenuItem>
                            <MenuItem onClick={() => onDeleteNotificationClicked(notification)}>Delete</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                  >
                    <Text
                      block
                      align='center'
                      italic
                    >
                      No notification
                    </Text>
                  </TableCell>
                </TableRow>
              )
          }
        </TableBody>
      </Table>
    </div>
  );
}

export const NotificationsTable = observer(NotificationsTableImpl);
